import { verifyLeaf } from "@ondo/shared";
import type { MerkleStep, Sha256, WitnessLeaf, WitnessVerdict } from "@ondo/shared";

import { toWitnessPanel } from "../witness/witness.model.ts";
import type { WitnessLineView } from "../witness/witness.model.ts";
import { BUNDLE_FORMAT, HEX_64, PROOF_COPY } from "./proof.constants.ts";

/**
 * The proof page's logic: read a bundle somebody dropped on it, check every
 * entry, and say what the hours and their anchors are.
 *
 * THE INPUT IS HOSTILE BY DEFINITION. A bundle is a file a reader got from
 * somewhere, and the whole reason for the page is that it may have been
 * tampered with on the way. So `parseBundle` admits exactly one shape and
 * refuses everything else with a reason, and nothing downstream trusts a
 * field it did not see checked. A bundle that fails here is "not a bundle";
 * a bundle that parses and whose hashes do not recompute is "a bundle whose
 * entries do not recompute", which is a different and more interesting answer.
 *
 * THE CHECK IS THE SAME CODE THE RECORD TAB RUNS. `verifyLeaf` lives in
 * `@ondo/shared` and is held to `CRYPTOGRAPHY.md`'s vectors by its own test;
 * this page adds one check the tab cannot make, the chain between neighbouring
 * hours, because a reader with a whole bundle holds both ends of it.
 */

export type BundleParse =
  | { kind: "invalid"; reason: string }
  | { kind: "bundle"; workId: string; exportedAt: string | null; leaves: WitnessLeaf[] };

const isString = (value: unknown): value is string => typeof value === "string";
const isNullableString = (value: unknown): value is string | null =>
  value === null || typeof value === "string";
const isHex = (value: unknown): value is string => typeof value === "string" && HEX_64.test(value);
const isNullableHex = (value: unknown): value is string | null => value === null || isHex(value);
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

function isPath(value: unknown): value is MerkleStep[] | null {
  if (value === null) return true;
  if (!Array.isArray(value)) return false;
  return value.every(
    (step) =>
      isRecord(step) && (step.side === "left" || step.side === "right") && isHex(step.hash),
  );
}

function isRevealed(value: unknown): value is WitnessLeaf["revealed"] {
  if (value === null) return true;
  if (!isRecord(value)) return false;
  return Object.values(value).every(
    (field) => isRecord(field) && isString(field.text) && isHex(field.salt),
  );
}

function isAnchor(value: unknown): value is WitnessLeaf["anchor"] {
  if (value === null) return true;
  if (!isRecord(value)) return false;
  return (
    isNullableString(value.submittedAt) &&
    (value.blockHeight === null || typeof value.blockHeight === "number") &&
    isNullableString(value.confirmedAt) &&
    (value.calendars === null ||
      (Array.isArray(value.calendars) && value.calendars.every(isString))) &&
    isNullableString(value.proof)
  );
}

/**
 * One entry, or nothing. `subject` and `state` are admitted as any string: a
 * newer migration may have added one before this build has a word for it, and
 * the labels fall back to the spelling — the tolerance `packages/shared` asks
 * of every reader of an enum. The HASHES are not tolerated: a bundle whose
 * leaf hash is not sixty-four hex characters cannot be checked and is refused.
 */
function toLeaf(value: unknown): WitnessLeaf | null {
  if (!isRecord(value)) return null;
  if (
    !isString(value.id) ||
    !isString(value.subject) ||
    !isString(value.subjectId) ||
    !isString(value.state) ||
    !isString(value.witnessedAt) ||
    !isNullableString(value.preimage) ||
    !isNullableHex(value.salt) ||
    !isHex(value.leafHash) ||
    !isString(value.periodStart) ||
    !(value.position === null || typeof value.position === "number") ||
    !isNullableHex(value.previousRoot) ||
    !isNullableHex(value.root) ||
    !isPath(value.path) ||
    !isAnchor(value.anchor) ||
    !isRevealed(value.revealed) ||
    !isNullableString(value.shreddedAt)
  ) {
    return null;
  }
  return {
    id: value.id,
    subject: value.subject as WitnessLeaf["subject"],
    subjectId: value.subjectId,
    state: value.state,
    witnessedAt: value.witnessedAt,
    preimage: value.preimage,
    salt: value.salt,
    leafHash: value.leafHash,
    periodStart: value.periodStart,
    position: value.position,
    previousRoot: value.previousRoot,
    root: value.root,
    path: value.path,
    anchor: value.anchor,
    revealed: value.revealed,
    shreddedAt: value.shreddedAt,
  };
}

export function parseBundle(text: string): BundleParse {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { kind: "invalid", reason: PROOF_COPY.notJson };
  }
  if (!isRecord(parsed) || !isString(parsed.format)) {
    return { kind: "invalid", reason: PROOF_COPY.notBundle };
  }
  if (parsed.format !== BUNDLE_FORMAT) {
    return { kind: "invalid", reason: PROOF_COPY.unknownFormat };
  }
  if (!isString(parsed.workId) || !Array.isArray(parsed.leaves)) {
    return { kind: "invalid", reason: PROOF_COPY.notBundle };
  }
  if (parsed.leaves.length === 0) {
    return { kind: "invalid", reason: PROOF_COPY.noLeaves };
  }
  const leaves: WitnessLeaf[] = [];
  for (const [index, candidate] of parsed.leaves.entries()) {
    const leaf = toLeaf(candidate);
    if (leaf === null) {
      return { kind: "invalid", reason: `${PROOF_COPY.badLeaf} (entry ${index + 1}).` };
    }
    leaves.push(leaf);
  }
  return {
    kind: "bundle",
    workId: parsed.workId,
    exportedAt: isString(parsed.exportedAt) ? parsed.exportedAt : null,
    leaves,
  };
}

/** Every entry's verdict, keyed by id. A shredded entry verifies as shredded, not as failed. */
export async function checkLeaves(
  leaves: readonly WitnessLeaf[],
  sha256: Sha256,
): Promise<Map<string, WitnessVerdict>> {
  const verdicts = new Map<string, WitnessVerdict>();
  for (const leaf of leaves) {
    if (leaf.preimage === null) {
      verdicts.set(leaf.id, { kind: "shredded" });
      continue;
    }
    try {
      verdicts.set(
        leaf.id,
        await verifyLeaf(
          {
            preimage: leaf.preimage,
            salt: leaf.salt,
            leafHash: leaf.leafHash,
            path: leaf.path,
            root: leaf.root,
            revealed: leaf.revealed,
          },
          sha256,
        ),
      );
    } catch (error) {
      // A malformed hex string or an unreadable step is a failed check on
      // this entry, never a crash of the page: the reader is owed the other
      // entries' verdicts regardless.
      verdicts.set(leaf.id, {
        kind: "failed",
        reason: error instanceof Error ? error.message : String(error),
      });
    }
  }
  return verdicts;
}

export type HourChain = "linked" | "broken" | "no-neighbour" | "open";

export interface HourView {
  periodStart: string;
  root: string | null;
  previousRoot: string | null;
  entries: number;
  chain: HourChain;
  anchor:
    | { kind: "open" }
    | { kind: "pending" }
    | { kind: "anchored"; blockHeight: number };
  /** The receipt as base64, when the bundle carries one for this hour. */
  receipt: string | null;
  receiptFilename: string | null;
  verifyCommand: string | null;
}

const HOUR_MS = 60 * 60 * 1000;

/** The file a reader hands to the `ots` tool, named by the hour it is about. */
export function receiptFilename(periodStart: string): string {
  return `ondo-root-${periodStart.replace(/[:.]/g, "-")}.ots`;
}

/** The exact command for step four, with the root filled in. */
export function verifyCommand(root: string, filename: string): string {
  return `ots verify -d ${root} ${filename}`;
}

/**
 * The bundle's hours, oldest first, each with its chain link checked against
 * the hour before it WHERE THE BUNDLE HOLDS BOTH. The server did not say the
 * hours are linked; the reader's own copy of two roots does, or does not.
 */
export function hoursOf(leaves: readonly WitnessLeaf[]): HourView[] {
  const byHour = new Map<string, WitnessLeaf[]>();
  for (const leaf of leaves) {
    const group = byHour.get(leaf.periodStart) ?? [];
    group.push(leaf);
    byHour.set(leaf.periodStart, group);
  }

  const rootOfHour = new Map<number, string>();
  for (const [periodStart, group] of byHour) {
    const root = group.find((leaf) => leaf.root !== null)?.root ?? null;
    const at = Date.parse(periodStart);
    if (root !== null && !Number.isNaN(at)) rootOfHour.set(at, root);
  }

  return [...byHour.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([periodStart, group]): HourView => {
      const root = group.find((leaf) => leaf.root !== null)?.root ?? null;
      const previousRoot = group.find((leaf) => leaf.previousRoot !== null)?.previousRoot ?? null;
      const at = Date.parse(periodStart);
      const before = Number.isNaN(at) ? undefined : rootOfHour.get(at - HOUR_MS);

      const chain: HourChain =
        root === null
          ? "open"
          : before === undefined
            ? "no-neighbour"
            : before === previousRoot
              ? "linked"
              : "broken";

      const anchorRow = group.find((leaf) => leaf.anchor !== null)?.anchor ?? null;
      const blockHeight = anchorRow?.blockHeight ?? null;
      const receipt = anchorRow?.proof ?? null;
      const filename = receipt === null ? null : receiptFilename(periodStart);

      return {
        periodStart,
        root,
        previousRoot,
        entries: group.length,
        chain,
        anchor:
          root === null
            ? { kind: "open" }
            : blockHeight === null
              ? { kind: "pending" }
              : { kind: "anchored", blockHeight },
        receipt,
        receiptFilename: filename,
        verifyCommand: root === null || filename === null ? null : verifyCommand(root, filename),
      };
    });
}

export type ProofSummary = Record<WitnessVerdict["kind"], number>;

/**
 * The one sentence a reader came for, decided here so the view prints it.
 * "clean" is every entry recomputing — anchored or merely waiting for its
 * hour; "problems" is at least one that does not. A shredded entry is neither:
 * it is noted, and it counts against nothing.
 */
export interface ProofVerdict {
  kind: "clean" | "problems";
  title: string;
  detail: string;
  note: string | null;
}

export interface ProofReport {
  workId: string;
  exportedAt: string | null;
  verdict: ProofVerdict;
  lines: WitnessLineView[];
  summary: ProofSummary;
  anchored: number;
  total: number;
  hours: HourView[];
}

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function verdictOf(summary: ProofSummary, anchored: number): ProofVerdict {
  const words = PROOF_COPY.verdict;
  const entries = (n: number) => count(n, words.entriesOne, words.entriesMany);
  const note =
    summary.shredded === 0
      ? null
      : words.shreddedNote.replace(
          "{shredded}",
          `${entries(summary.shredded)} ${summary.shredded === 1 ? words.isOne : words.isMany}`,
        );

  if (summary.failed > 0) {
    const one = summary.failed === 1;
    return {
      kind: "problems",
      title: words.problemsTitle
        .replace("{failed}", entries(summary.failed))
        .replace("{does}", one ? words.doesOne : words.doesMany),
      detail: words.problemsDetail.replace("{those}", one ? words.thatOne : words.thatMany),
      note,
    };
  }

  const checked = summary.verified + summary["awaiting-close"];
  const waiting = checked - anchored;
  return {
    kind: "clean",
    title: words.cleanTitle,
    detail:
      waiting <= 0
        ? words.cleanAllAnchored
        : words.cleanSomeWaiting.replace(
            "{waiting}",
            `${entries(waiting)} ${waiting === 1 ? words.isOne : words.isMany}`,
          ),
    note,
  };
}

export function toReport(
  bundle: Extract<BundleParse, { kind: "bundle" }>,
  verdicts: ReadonlyMap<string, WitnessVerdict>,
): ProofReport {
  // The Record tab's own lines, so the two screens cannot describe one leaf
  // two ways. The tab never sees an empty list here: `parseBundle` refused it.
  const panel = toWitnessPanel({ kind: "loaded", leaves: bundle.leaves, verdicts });
  const lines = panel.kind === "listed" ? panel.lines : [];

  const summary: ProofSummary = { verified: 0, "awaiting-close": 0, failed: 0, shredded: 0 };
  for (const verdict of verdicts.values()) summary[verdict.kind] += 1;

  const anchored = panel.kind === "listed" ? panel.anchored : 0;
  return {
    workId: bundle.workId,
    exportedAt: bundle.exportedAt,
    verdict: verdictOf(summary, anchored),
    lines,
    summary,
    anchored,
    total: bundle.leaves.length,
    hours: hoursOf(bundle.leaves),
  };
}

/** Base64 to bytes, for a receipt on its way to a file. Browser-only input, pure output. */
export function decodeBase64(text: string): Uint8Array<ArrayBuffer> {
  const binary = atob(text);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
